import { useCallback, useEffect, useState } from 'react'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { ArchigmaMark } from '../components/Logo'
import { Button } from '../components/ui/button'
import { useAuth } from '../lib/auth'
import {
  confirmLoreOrganizationOperation,
  previewLoreOrganizationOperation,
} from '../lib/endpoints/auth'
import { loreOrganizationOperationCode } from '../lib/loreAuth'
import type { LoreOrganizationOperation } from '../lib/schemas'

const confirmationStorageKey = 'lore.organization-operation.code'

function confirmationCode(): string | null {
  if (typeof window === 'undefined') return null
  const fragmentCode = loreOrganizationOperationCode(window.location.hash, null)
  try {
    if (fragmentCode) {
      window.sessionStorage.setItem(confirmationStorageKey, fragmentCode)
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`,
      )
      return fragmentCode
    }
    const storedCode = window.sessionStorage.getItem(confirmationStorageKey)
    return loreOrganizationOperationCode('', storedCode)
  } catch {
    return fragmentCode
  }
}

function clearConfirmationCode(): void {
  try {
    window.sessionStorage.removeItem(confirmationStorageKey)
  } catch {
    // The code still expires server-side if browser storage is unavailable.
  }
}

function actionDescription(operation: LoreOrganizationOperation): string {
  const action = operation.action
  if (action.kind === 'invite_member') {
    return `Zaproszenie ${action.email} z rola ${action.role}.`
  }
  return `Usuniecie ${action.email} z organizacji.`
}

export function LoreOrganizationOperationPage() {
  const auth = useAuth()
  const [code] = useState(confirmationCode)
  const [operation, setOperation] = useState<LoreOrganizationOperation | null>(
    null,
  )
  const [working, setWorking] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!code) return
    setLoading(true)
    setError(null)
    try {
      setOperation(await previewLoreOrganizationOperation(code))
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Nie udalo sie odczytac operacji LORE',
      )
    } finally {
      setLoading(false)
    }
  }, [code])

  useEffect(() => {
    if (auth.status === 'authenticated') void load()
  }, [auth.status, load])

  useEffect(() => {
    if (
      operation?.status === 'succeeded' ||
      operation?.status === 'failed' ||
      operation?.status === 'expired' ||
      operation?.status === 'needs_review'
    ) {
      clearConfirmationCode()
    }
  }, [operation?.status])

  const login = () => {
    const returnTo = window.location.pathname
    window.location.assign(`/login?return_to=${encodeURIComponent(returnTo)}`)
  }

  const confirm = async () => {
    if (!code) return
    setWorking(true)
    setError(null)
    try {
      setOperation(await confirmLoreOrganizationOperation(code))
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Nie udalo sie wykonac operacji LORE',
      )
    } finally {
      setWorking(false)
    }
  }

  return (
    <AuthShell>
      <div
        className="space-y-5 text-center"
        data-testid="lore-organization-operation"
      >
        <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-foreground">
          <ArchigmaMark className="h-7 w-5 text-foreground" />
          Potwierdzenie LORE
        </h1>

        {!code && (
          <NoticeBanner tone="error" message="Nieprawidlowy link operacji." />
        )}
        {error && <NoticeBanner tone="error" message={error} />}
        {operation?.status === 'succeeded' && (
          <NoticeBanner
            tone="success"
            message="Operacja zostala wykonana. Mozesz zamknac to okno i wrocic do LORE Archigma."
          />
        )}
        {operation?.status === 'failed' && (
          <NoticeBanner
            tone="error"
            message={
              operation.failureCode === 'provider_outcome_unknown'
                ? 'Nie mozna potwierdzic wyniku providera. Odswiez katalog organizacji przed utworzeniem kolejnej operacji.'
                : 'Dostawca logowania odrzucil operacje. Wroc do LORE Archigma i odswiez dane.'
            }
          />
        )}
        {operation?.status === 'expired' && (
          <NoticeBanner
            tone="error"
            message="Ta operacja wygasla. Utworz nowa w LORE Archigma."
          />
        )}
        {operation?.status === 'processing' && (
          <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Operacja jest przetwarzana. Nie zatwierdzaj jej ponownie.
          </p>
        )}
        {operation?.status === 'needs_review' && (
          <NoticeBanner
            tone="error"
            message="Proces zostal przerwany przed zapisaniem wyniku. Odswiez katalog organizacji i sprawdz stan przed utworzeniem kolejnej operacji."
          />
        )}

        {code && auth.status === 'unauthenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Zaloguj sie tym samym kontem Archigma, ktore przygotowalo operacje
              w LORE Archigma.
            </p>
            <Button className="w-full" size="lg" onClick={login}>
              Zaloguj sie
            </Button>
          </>
        )}

        {code && auth.status === 'authenticated' && loading && (
          <p className="text-sm text-muted-foreground">
            Sprawdzanie operacji...
          </p>
        )}

        {operation && auth.status === 'authenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Konto: <strong>{auth.user.email}</strong>
            </p>
            <p className="rounded-md border border-border bg-muted/40 p-4 text-sm text-foreground">
              {actionDescription(operation)}
            </p>
            {operation.status === 'pending' && (
              <Button
                className="w-full"
                size="lg"
                disabled={working}
                onClick={() => void confirm()}
              >
                {working ? 'Wykonywanie...' : 'Potwierdz operacje'}
              </Button>
            )}
            {operation.status === 'processing' && (
              <Button
                className="w-full"
                size="lg"
                variant="outline"
                disabled={loading}
                onClick={() => void load()}
              >
                Odswiez status
              </Button>
            )}
          </>
        )}

        {auth.status === 'loading' && (
          <p className="text-sm text-muted-foreground">Sprawdzanie sesji...</p>
        )}
        {auth.status === 'apiUnavailable' && (
          <NoticeBanner
            tone="error"
            message="Serwis logowania jest chwilowo niedostepny."
          />
        )}
      </div>
    </AuthShell>
  )
}
